import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawProductsApi, UploadRawProductImagesRequest, UploadRawProductImageResponse } from "../api/supplier/rawProductsApi";
import { showToast } from "@/app/components/CustomToastContainer/toastService";
import { GenerateThreeDModelPayload, GenerateThreeDModelResponse, threeDModelApi } from "../api/supplier/threeDModelApi";

interface UseGetAllRawProductsOptions {
    /**
     * If true → fetch automatically when vendor is available
     * If false → fetch only via refetch()
     */
    autoFetch?: boolean;
    /**
     * If true → fetch automatically when vendor is available
     * If false → fetch only via refetch()
     */
    autoPolling?: boolean;
}

interface UseGetRawProductByIdOptions {
    /**
     * If true → fetch automatically when vendor is available
     * If false → fetch only via refetch()
     */
    autoFetch?: boolean;
    /**
     * If true → fetch automatically when vendor is available
     * If false → fetch only via refetch()
     */
    autoPolling?: boolean;
}

export const useGetAllRawProducts = (options: UseGetAllRawProductsOptions = {}, params: any = {}) => {
    const { autoFetch = true, autoPolling = false } = options;
    return useQuery({
        queryKey: ["rawProducts", params],
        queryFn: () => rawProductsApi.getAllRawProducts(params),
        enabled: Boolean(autoFetch),
        retry(failureCount, error) {
            console.warn(`Retrying raw products fetch: attempt ${failureCount}`, error);
            return failureCount < 3;
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchInterval: autoPolling ? 30000 : false,
    });
};

export const useGetRawProductById = (productId: string, options: UseGetRawProductByIdOptions = {}) => {
    const { autoFetch = true, autoPolling = false } = options;
    return useQuery({
        queryKey: ["rawProductById", productId],
        queryFn: () => {
            if (!productId) {
                throw new Error("Product ID is required");
            }
            return rawProductsApi.getRawProductById(productId);
        },
        enabled: Boolean(autoFetch),
        retry(failureCount, error) {
            console.warn(`Retrying raw product fetch: attempt ${failureCount}`, error);
            return failureCount < 3;
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchInterval: autoPolling ? 30000 : false,
    });
};

export const useDeleteRawProduct = () => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, any, { previousProducts?: any }>({
        mutationFn: async (id: any) => {
            await rawProductsApi.deleteRawProduct(id);
        },
        onMutate: async (id: any) => {
            await queryClient.cancelQueries({ queryKey: ['rawProducts'] });

            const previousProducts = queryClient.getQueriesData<any>({ queryKey: ['rawProducts'] });

            queryClient.setQueriesData<any>({ queryKey: ['rawProducts'] }, (old: any) => {
                if (!old) return old;
                if (Array.isArray(old)) {
                    return old.filter((product: any) => product?.productSkuId !== id);
                } else if (old.data && Array.isArray(old.data)) {
                    return { ...old, data: old.data.filter((product: any) => product?.productSkuId !== id) };
                }
                return old;
            });

            showToast({
                content: "Product deleted successfully"
            });

            return { previousProducts };
        },
        onError: (_err, _id, context) => {
            if (context?.previousProducts) {
                context.previousProducts.forEach(([queryKey, data]: [any, any]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            showToast({
                content: "Failed to delete product"
            });
        },
        onSettled: () => {
            // no-op
        },
    });
};

const applyDotNotationUpdate = (obj: any, updates: Record<string, any>) => {
    const clone = structuredClone(obj);

    Object.entries(updates).forEach(([path, value]) => {
        const keys = path.split('.');
        let current = clone;

        keys.forEach((key, index) => {
            if (index === keys.length - 1) {
                current[key] = value;
            } else {
                current[key] ??= {};
                current = current[key];
            }
        });
    });

    return clone;
};


type PatchArgs = {
    productId: string;
    payload: Record<string, any>;
};

export const usePatchRawProduct = () => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, PatchArgs, { previousProductById?: any; previousRawProducts?: any }>({
        mutationFn: async ({ productId, payload }) => {
            await rawProductsApi.patchRawProduct(productId, payload);
        },

        onMutate: async ({ productId, payload }) => {
            // Cancel any outgoing refetches for both queries
            await queryClient.cancelQueries({ queryKey: ['rawProductById', productId] });
            await queryClient.cancelQueries({ queryKey: ['rawProducts'] });

            // Snapshot the previous values
            const previousProductById = queryClient.getQueryData<any>(['rawProductById', productId]);
            const previousRawProducts = queryClient.getQueriesData<any>({ queryKey: ['rawProducts'] });

            // Optimistically update 'rawProductById'
            if (previousProductById?.length) {
                const optimisticProduct = [
                    applyDotNotationUpdate(previousProductById[0], payload)
                ];
                queryClient.setQueryData(['rawProductById', productId], optimisticProduct);
            }

            // Optimistically update 'rawProducts' list
            queryClient.setQueriesData<any>({ queryKey: ['rawProducts'] }, (old: any) => {
                if (!old) return old;

                let currentProductCode: string | undefined;
                const productsArray = Array.isArray(old) ? old : (old.data || []);

                // First pass: find the product code of the item being updated
                const productToUpdate = productsArray.find((p: any) => p.productSkuId === productId);
                if (productToUpdate) {
                    currentProductCode = productToUpdate.productCode;
                }

                // If updating 'isMaster' to true, we need to unset it for others in the same group
                const shouldResetOthers = payload.masterSKU === "yes" && currentProductCode;

                const newRawProducts = productsArray.map((product: any) => {
                    // Update the target product
                    if (product.productSkuId === productId) {
                        return applyDotNotationUpdate(product, payload);
                    }
                    // If we need to reset others in the same group
                    if (shouldResetOthers && product.productCode === currentProductCode && product.masterSKU === "yes") {
                        return { ...product, masterSKU: "no" };
                    }
                    return product;
                });

                if (Array.isArray(old)) {
                    return newRawProducts;
                } else if (old.data && Array.isArray(old.data)) {
                    return { ...old, data: newRawProducts };
                }
                return old;
            });

            return { previousProductById, previousRawProducts };
        },

        onError: (_err, { productId }, context) => {
            if (context?.previousProductById) {
                queryClient.setQueryData(['rawProductById', productId], context.previousProductById);
            }
            if (context?.previousRawProducts) {
                context.previousRawProducts.forEach(([queryKey, data]: [any, any]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }

            showToast({
                content: 'Failed to update product',
            });
        },

        onSettled: (_data, _error, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['rawProductById', productId] });
            queryClient.invalidateQueries({ queryKey: ['rawProducts'] });
        },
    });
};

export const useUploadRawProductImages = () => {
    return useMutation<UploadRawProductImageResponse, unknown, UploadRawProductImagesRequest, { previousProduct?: any }>({
        mutationFn: async (payload) => {
            return await rawProductsApi.uploadRawProductImage(payload);
        },
        onSuccess: (data) => {
            console.log("Presigned URLs:", data.presignedUrls);
        },
        onError: (_err, _payload, _context) => {
            showToast({
                content: 'Failed to upload product images',
            });
        },
    });
};

export const useGenerateThreeDModelForPillowAndRug = () => {
    const queryClient = useQueryClient();

    return useMutation<GenerateThreeDModelResponse, unknown, GenerateThreeDModelPayload, { previousProduct?: any }>({
        mutationFn: async (payload) => {
            return await threeDModelApi.generateThreeDModelForPillowAndRug(payload);
        },
        onSuccess: (data, payload) => {
            console.log("GenerateThreeDModelResponse:", data);
            showToast({
                content: 'Product updated successfully',
            });
            queryClient.setQueryData(
                ["rawProductById", payload.productSkuId],
                (old: any[]) => {
                    if (!old) return old;

                    return old.map((product) => {
                        if (product.productSkuId !== payload.productSkuId) {
                            return product;
                        }

                        return {
                            ...product,
                            threedImage: data.threedImage,
                            isThreeDUrlValid: true,
                        };
                    });
                }
            );

        },
        onError: (_err, payload, context) => {
            if (context?.previousProduct) {
                queryClient.setQueryData(
                    ['rawProductById', payload.productSkuId],
                    context.previousProduct
                );
            }

            showToast({
                content: 'Failed to upload product images',
            });
        },

        onSettled: (_data, _error, payload) => {
            queryClient.invalidateQueries({
                queryKey: ['rawProductById', payload.productSkuId],
            });
        },
    });
};
